import { type QBankStructureLevel } from '@exam-management/qbs-provider';
import {
  getLocationDefaultValue,
  getQContentCreateSchema,
  type DetailSectionType,
  type InformationSectionType,
  type LocationSectionType,
  type OwnerSectionType,
  type PropertiesSectionType,
} from '@exam-management/qcontent-management';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loading } from '@shared/components';
import type { PropsWithChildren } from '@shared/utils';
import { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useLocation } from 'react-router-dom';

export type QContentCreateFormContextData = LocationSectionType &
  DetailSectionType &
  InformationSectionType &
  OwnerSectionType &
  PropertiesSectionType;

const LOCATION_DEFAULT_VALUES = {
  location: {
    LOQ: {
      selected: undefined,
      value: undefined,
    },
  },
};

const INFORMATION_DEFAULT_VALUES = {
  information: {
    calculated: '',
    pValue: {
      selected: 'pInitial' as QContentCreateFormContextData['information']['pValue']['selected'],
      pInitial: '' as const,
      pStat: undefined,
    },
    bIndex: '' as const,
  },
};

const DETAIL_DEFAULT_VALUES = {
  detail: {
    details: '<p></p>',
    choices: [
      { value: '1', label: '<p></p>', order: 1 },
      { value: '2', label: '<p></p>', order: 2 },
      { value: '3', label: '<p></p>', order: 3 },
      { value: '4', label: '<p></p>', order: 4 },
    ],
    correctAnswer: { value: undefined },
  },
};

const PROPERTIES_DEFAULT_VALUES = {
  properties: {
    learningObjectiveStatement: undefined,
    cognitiveDomain: [],
    solution: '<p></p>',
    reference: '<p></p>',
  },
};

const OWNERS_DEFAULT_VALUES = {
  owners: {
    owner: '',
    audit1: '',
    audit2: '',
    audit3: '',
  },
};

export const DEFAULT_FORM_VALUES = {
  ...LOCATION_DEFAULT_VALUES,
  ...INFORMATION_DEFAULT_VALUES,
  ...DETAIL_DEFAULT_VALUES,
  ...PROPERTIES_DEFAULT_VALUES,
  ...OWNERS_DEFAULT_VALUES,
};

export interface QContentCreateFormProviderProps extends PropsWithChildren {
  qbsLevels: QBankStructureLevel[];
}

export default function QContentCreateFormProvider(
  props: Readonly<QContentCreateFormProviderProps>,
) {
  const { children, qbsLevels } = props;
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const CreateSingleQuestionSchema = getQContentCreateSchema(qbsLevels);
  const defaultLocationValues = getLocationDefaultValue(qbsLevels);
  const locationFields = useMemo(() => {
    return location?.state?.locationFields ?? {};
  }, [location]);

  const formContext = useForm<QContentCreateFormContextData>({
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      location: {
        ...DEFAULT_FORM_VALUES.location,
        ...defaultLocationValues,
      },
    },
    resolver: zodResolver(CreateSingleQuestionSchema),
  });

  useEffect(() => {
    formContext.reset(
      {
        ...DEFAULT_FORM_VALUES,
        location: {
          ...DEFAULT_FORM_VALUES.location,
          ...defaultLocationValues,
          ...locationFields,
        },
      },
      {
        keepDefaultValues: true,
      },
    );
    setIsLoading(false);
  }, [locationFields]);

  if (isLoading) {
    return <Loading height='100%' />;
  }

  return <FormProvider {...formContext}>{children ?? <Outlet />}</FormProvider>;
}
